import type { FC, ReactNode } from "react";

import {
  ArrowRightIcon,
  SubduedText,
  LandingSection,
  LiveDot,
  Panel,
  PRIMARY_BUTTON_CLASS_NAME,
  SectionHeading,
  WindowFrame,
} from "./landingPrimitives";

interface MeshClient {
  detail: string;
  icon: ReactNode;
  name: string;
}

const TerminalIcon: FC = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="4.5"
      width="18"
      height="15"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="m7.5 10 2.5 2-2.5 2M12.5 14.5h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DesktopIcon: FC = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="4"
      width="18"
      height="12.5"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M9 20h6M12 16.5V20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChatIcon: FC = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 19 17h-7.5L7 20.5V17H5a1.5 1.5 0 0 1-1.5-1.5V7A1.5 1.5 0 0 1 5 5.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M8 10.5h8M8 13.5h5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CodeIcon: FC = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="m8.5 8-4 4 4 4M15.5 8l4 4-4 4M13.5 5.5l-3 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LEFT_CLIENTS: readonly MeshClient[] = [
  { detail: "CLI", icon: <TerminalIcon />, name: "Claude Code" },
  { detail: "Desktop app", icon: <DesktopIcon />, name: "Claude Desktop" },
];

const RIGHT_CLIENTS: readonly MeshClient[] = [
  { detail: "Plugin", icon: <ChatIcon />, name: "ChatGPT" },
  { detail: "CLI and app", icon: <CodeIcon />, name: "Codex" },
];

const SAMPLE_SKILLS = [
  { name: "code-review-checklist", version: "v12" },
  { name: "incident-runbook", version: "v5" },
  { name: "brand-voice", version: "v8" },
  { name: "release-notes", version: "v3" },
] as const;

const MESH_POINTS = [
  {
    body: "Claude and ChatGPT load the same skill. Update it once and every client picks up the new version.",
    title: "Write once, use everywhere",
  },
  {
    body: "Every edit becomes a new version with its history intact. Archive instead of delete, and restore any earlier revision.",
    title: "Versioned, never lost",
  },
  {
    body: "Claude and ChatGPT connect by signing in to Arti; command-line clients can use a scoped token instead. Templates and reference files travel with the skill.",
    title: "Sign in with Arti",
  },
] as const;

const MeshClientCard: FC<{ client: MeshClient; side: "left" | "right" }> = ({
  client,
  side,
}) => (
  <div className="relative">
    <Panel className="flex items-center gap-3 p-4">
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-slate-700 bg-slate-900/60 text-slate-200">
        {client.icon}
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-white">
          {client.name}
        </div>
        <div className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
          {client.detail}
        </div>
      </div>
      <span
        className="ml-auto h-1.5 w-1.5 flex-shrink-0 bg-emerald-400"
        aria-hidden="true"
      />
    </Panel>
    <span
      className={`pointer-events-none absolute top-1/2 hidden h-px w-10 lg:block ${
        side === "left"
          ? "left-full bg-emerald-500/50"
          : "right-full bg-emerald-500/50"
      }`}
      aria-hidden="true"
    />
  </div>
);

const MeshHub: FC = () => (
  <WindowFrame
    title="Skill Mesh · MCP"
    status={<LiveDot label="Synced" />}
    footer={
      <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
        <span>4 clients · 1 source of truth</span>
        <span className="text-emerald-400">OAuth</span>
      </div>
    }
  >
    <ul className="divide-y divide-slate-800">
      {SAMPLE_SKILLS.map((skill) => (
        <li key={skill.name} className="flex items-center gap-3 px-4 py-3">
          <span
            className="h-1.5 w-1.5 flex-shrink-0 bg-emerald-400"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-200">
            {skill.name}
          </span>
          <span className="flex-shrink-0 border border-slate-700 px-1.5 py-px font-mono text-[10px] text-slate-400">
            {skill.version}
          </span>
        </li>
      ))}
    </ul>
  </WindowFrame>
);

export const LandingSkillMeshSection: FC = () => (
  <LandingSection id="skill-mesh" band>
    <SectionHeading
      align="center"
      eyebrow="Skill Mesh"
      title={
        <>
          Your company&apos;s skills,{" "}
          <SubduedText>in every AI client.</SubduedText>
        </>
      }
      body="Skill Mesh is Arti's MCP server for shared skills. Write your review checklist, runbook or house style once, and Claude and ChatGPT both work from the same versioned copy."
    />

    <div className="mx-auto mt-14 grid max-w-5xl items-center gap-4 lg:grid-cols-[1fr_1.25fr_1fr] lg:gap-10">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
        {LEFT_CLIENTS.map((client) => (
          <MeshClientCard key={client.name} client={client} side="left" />
        ))}
      </div>
      <MeshHub />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
        {RIGHT_CLIENTS.map((client) => (
          <MeshClientCard key={client.name} client={client} side="right" />
        ))}
      </div>
    </div>

    <div className="mt-14 grid gap-4 md:grid-cols-3">
      {MESH_POINTS.map((point) => (
        <Panel key={point.title} as="article" className="p-6">
          <h3 className="font-display text-lg font-bold normal-case !tracking-tight text-white">
            {point.title}
          </h3>
          <p className="mt-3 text-sm leading-6 text-slate-400">{point.body}</p>
        </Panel>
      ))}
    </div>

    <div className="mt-10 flex justify-center">
      <a href="#auth" className={PRIMARY_BUTTON_CLASS_NAME}>
        Get in touch
        <ArrowRightIcon />
      </a>
    </div>
  </LandingSection>
);
